import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableComponent } from '@markant-platform/shared/table/feature';
import { TableDraft } from '@markant-platform/shared/table/model';
import { UsageAggregationFilter, UsageAggregationPage } from '@markant-platform/usage/data-access';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { deepEqual } from '@markant-platform/shared/util-helpers';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'markant-platform-usage-report-table',
  standalone: true,
  imports: [CommonModule, TableComponent],
  templateUrl: './usage-report-table.component.html',
  styleUrls: ['./usage-report-table.component.scss'],
})
export class UsageReportTableComponent extends Table implements OnInit {
  @Input({ required: true }) set draft(draft: TableDraft | null) {
    if (!draft) {
      return;
    }

    this.tableDraft = draft;
    this.reload();
  }

  @Input() set filter(filter: UsageAggregationFilter | undefined) {
    if (filter) {
      this.filterChanged$.next(filter);
    }
  }

  @Input({ required: true }) loadFn!: (
    filter: UsageAggregationFilter,
    index: number,
    size: number
  ) => Observable<UsageAggregationPage>;

  private filterChanged$: Subject<UsageAggregationFilter> = new ReplaySubject<UsageAggregationFilter>(1);

  constructor(private readonly snackbarService: SnackbarService) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    combineLatest([this.filterChanged$, this.tableParams$.pipe(distinctUntilChanged(deepEqual))])
      .pipe(
        tap(() => this.isLoading$.next(true)),
        switchMap(([filter, { page }]) => this.loadFn(filter, page.index - 1, page.size)),
        catchError(error => {
          this.isLoading$.next(false);
          this.snackbarService.showErrorSnackbarWithTransloco('usage.report.error', { traceId: error?.error?.traceId });
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ result, totalCount }) => {
        this.totalCount = totalCount;
        this.reload(result);
      });
  }
}
